import type { CSSProperties, ReactNode } from 'react';

export const mono = "'IBM Plex Mono', monospace";
export const sans = "'Rajdhani', sans-serif";

export function Eyebrow({
  children,
  color = '#4fd8c2',
  style,
}: {
  children: ReactNode;
  color?: string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1.8, lineHeight: 1.4, color, ...style }}>
      {children}
    </div>
  );
}

export function Lede({ children, color = '#8fa4b5' }: { children: ReactNode; color?: string }) {
  return <div style={{ fontFamily: mono, fontSize: 13, color, lineHeight: 1.55 }}>{children}</div>;
}

/**
 * The one line on a screen that has to be read (Marek, 2026-09-29: "more hero text and buttons so
 * it's easier to see what needs to be read and pressed"): big, plain and bright.
 */
export function Hero({ children, color = '#eef4f8', style }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  return (
    <div style={{ fontFamily: sans, fontSize: 24, fontWeight: 700, letterSpacing: 0.4, lineHeight: 1.25, color, ...style }}>
      {children}
    </div>
  );
}

/** A guest's post, under their call sign in a list: smaller and quieter, and always one line. */
export function PostLine({ children, color = '#7d8e9c', style }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  if (!children) return null;
  return (
    <span
      style={{
        display: 'block',
        fontFamily: mono,
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: 1,
        lineHeight: 1.4,
        color,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function PrimaryButton({
  onClick,
  children,
  bg = '#4fd8c2',
  fg = '#06231e',
  disabled = false,
  big = false,
}: {
  onClick?: () => void;
  children: ReactNode;
  bg?: string | undefined;
  fg?: string | undefined;
  disabled?: boolean;
  /** The one thing on the screen to press: full width, taller, larger type. */
  big?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        opacity: disabled ? 0.55 : 1,
        minHeight: big ? 60 : 52,
        ...(big ? { width: '100%' } : {}),
        borderRadius: 12,
        border: 'none',
        background: bg,
        color: fg,
        fontFamily: sans,
        fontSize: big ? 21 : 17,
        fontWeight: 700,
        letterSpacing: big ? 3.5 : 3,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  onClick,
  children,
  color,
  border,
  dashed = false,
}: {
  onClick?: () => void;
  children: ReactNode;
  color: string;
  border: string;
  dashed?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 48,
        borderRadius: 12,
        border: `${dashed ? '1.5px dashed' : '1px solid'} ${border}`,
        background: 'transparent',
        color,
        fontFamily: sans,
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: 3,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Pushes what follows to the bottom of a screen — but never all the way to nothing.
 *
 * On a tall phone `flex: 1` gives a generous gap. On a short one it collapses to zero
 * and the control above ends up sitting directly on the primary button, which reads as
 * one block and invites a mis-tap on the wrong one. The floor keeps them apart; the
 * screen scrolls if that costs a few pixels.
 */
export function Spacer({ min = 28 }: { min?: number } = {}) {
  return <div style={{ flex: 1, minHeight: min }} />;
}

